
import { useEffect } from 'react';
import SEOHead from '@/components/SEOHead';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import HeroSection from '@/components/home/HeroSection';
import FeaturesSection from '@/components/home/FeaturesSection';
import RolesSection from '@/components/home/RolesSection';
import ParentSection from '@/components/home/ParentSection';
import CTASection from '@/components/home/CTASection';
import AgentBetaSection from '@/components/home/AgentBetaSection';
import { useIsMobile } from '@/hooks/use-mobile';
import { UserRole } from '@/types/user';
import { useState } from 'react';
import { getNonParentSignupRoles } from '@/data/roleData';

const Index = () => {
  const { user: supabaseUser, loading: supabaseLoading, initialCheckComplete: supabaseInitialCheck } = useAuth();
  const { user: internalUser, loading: internalLoading, initialCheckComplete: internalInitialCheck } = useInternalAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [previewRole, setPreviewRole] = useState<UserRole>('player');

  // Wait for both auth systems to complete their initial checks
  const bothSystemsReady = supabaseInitialCheck && internalInitialCheck;
  const anySystemLoading = supabaseLoading || internalLoading;

  useEffect(() => {
    // Only redirect after both systems have completed their initial checks and are no longer loading
    if (bothSystemsReady && !anySystemLoading) {
      // Double-check localStorage to confirm user is actually logged in (prevents race condition on logout)
      const hasInternalSession = localStorage.getItem('internal_user_session');
      const hasFoundingAgentSession = localStorage.getItem('foundingAgentSession');
      
      if (internalUser && (hasInternalSession || hasFoundingAgentSession)) {
        console.log('Internal user authenticated, redirecting to home');
        // Small delay to ensure profile is loaded
        const timer = setTimeout(() => {
          navigate('/home');
        }, 100);
        return () => clearTimeout(timer);
      } else if (supabaseUser) {
        console.log('Supabase user authenticated, redirecting to home');
        // Small delay to ensure profile is loaded
        const timer = setTimeout(() => {
          navigate('/home');
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [bothSystemsReady, anySystemLoading, internalUser, supabaseUser, navigate]);

  // Show loading until both systems are ready
  if (!bothSystemsReady || anySystemLoading) {
    return (
      <div className="flex flex-col min-h-screen bg-black w-full">
        {!isMobile && <Header />}
        {isMobile && <MobileHeader />}
        
        <main className={`flex-grow flex items-center justify-center ${
          isMobile ? 'mt-16 mb-16' : 'mt-32'
        }`}>
          <div className="text-center text-white">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sports-green mx-auto mb-4"></div>
            <p className="text-lg">Loading...</p>
          </div>
        </main>
        
        {!isMobile && <Footer />}
        {isMobile && <MobileBottomNav />}
      </div>
    );
  }

  // Show the main landing page if no user is authenticated
  return (
    <div className="flex flex-col min-h-screen bg-black w-full">
      <SEOHead title="Home" description="Train. Compete. Build your legacy. Players Lockeroom is your sports management, community, and NIL tools platform." path="/" />
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className="flex-grow w-full">
        <HeroSection previewRole={previewRole} setPreviewRole={setPreviewRole} />
        <FeaturesSection />
        <RolesSection roles={getNonParentSignupRoles()} setPreviewRole={setPreviewRole} />
        <ParentSection />
        <AgentBetaSection />
        <CTASection />
      </main>
      
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default Index;
